// src/pages/VisitDetails.js
// Editable encounter container for CURRENT (active) visit only.
// If a previous visit is opened (explicit visitId in URL that isn't the scoped active),
// we redirect to the read-only summary page.

import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Tabs, Tab, Alert, Button, Stack } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import EncounterHeader from '../components/EncounterHeader';
import CitizenMedicalHistory from './citizen/CitizenMedicalHistory';
import CitizenExam from './citizen/CitizenExam';
import CitizenInvestigations from './citizen/CitizenInvestigations';
import CitizenPrescriptions from './citizen/CitizenPrescriptions';

// ---- tenant-safe navigation helpers ----
function getTenantBase() {
  try {
    const m = (window.location.pathname || '').match(/^\/t\/[^/]+/);
    return m ? m[0] : '';
  } catch {
    return '';
  }
}
function tenantUrl(path) {
  const base = getTenantBase();
  const p = String(path || '');
  const clean = p.startsWith('/') ? p : `/${p}`;
  return `${base}${clean}`.replace(/\/{2,}/g, '/');
}

export default function VisitDetails() {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();

  // ✅ memoized query params (prevents extra effect re-runs)
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [tab, setTab] = useState(0);
  const [visitId, setVisitId] = useState('');
  const [citizenId, setCitizenId] = useState('');

  // Resolve IDs
  useEffect(() => {
    const cid =
      params.citizenId ||
      query.get('citizenId') ||
      JSON.parse(localStorage.getItem('activeCitizen') || 'null')?.citizenId ||
      localStorage.getItem('activeCitizenId') ||
      '';

    // Prefer explicit visitId in URL (when viewing history); otherwise fall back to active
    const vidFromUrl = query.get('visitId') || '';

    // ✅ IMPORTANT: prefer citizen-scoped active visitId if present
    const scopedKey = cid ? `activeVisitId:${cid}` : '';
    const vidFromStore =
      (scopedKey ? localStorage.getItem(scopedKey) : '') ||
      localStorage.getItem('visitId') ||
      localStorage.getItem('activeVisitId') ||
      '';

    const vid = vidFromUrl || vidFromStore || '';

    setCitizenId(cid || '');
    setVisitId(vid || '');

    // 🔐 Redirect logic:
    // If there is an explicit visitId in URL AND it doesn't match the scoped active (or no scoped active),
    // treat as previous and go to read-only summary.
    if (cid && vidFromUrl) {
      const scopedActive = localStorage.getItem(`activeVisitId:${cid}`) || '';
      if (!scopedActive || scopedActive !== vidFromUrl) {
        // ✅ tenant-safe + correct route
        navigate(
          tenantUrl(
            `/citizen/${encodeURIComponent(cid)}/visit-summary?visitId=${encodeURIComponent(
              vidFromUrl
            )}`
          ),
          { replace: true, state: location.state }
        );
      }
      return;
    }

    // If there is no URL visitId but we somehow landed here with a visit that isn't scoped active,
    // also guard (rare, but keeps things consistent).
    if (cid && vid) {
      const scopedActive = localStorage.getItem(`activeVisitId:${cid}`) || '';
      if (!scopedActive || scopedActive !== vid) {
        // ✅ tenant-safe + correct route
        navigate(
          tenantUrl(
            `/citizen/${encodeURIComponent(cid)}/visit-summary?visitId=${encodeURIComponent(vid)}`
          ),
          { replace: true, state: location.state }
        );
      }
    }
  }, [params.citizenId, query, navigate, location.state]);

  const handleTabChange = (_e, newValue) => setTab(newValue);

  if (!visitId) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        No active visit selected.
      </Alert>
    );
  }

  const fromApprovals = location.state?.from === 'approvals';
  const backTo = location.state?.backTo || tenantUrl('/dashboard/approvals');

  return (
    <div className="p-6 bg-white dark:bg-[#181a1b] min-h-screen text-black dark:text-white">
      {fromApprovals && (
        <Stack direction="row" sx={{ mb: 1 }}>
          <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate(backTo)}>
            Back to Approvals
          </Button>
        </Stack>
      )}

      <EncounterHeader visitId={visitId} />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={handleTabChange} variant="scrollable" allowScrollButtonsMobile>
          <Tab label="Medical History" />
          <Tab label="Examination" />
          <Tab label="Investigations" />
          <Tab label="Prescriptions" />
        </Tabs>
      </Box>

      {tab === 0 && <CitizenMedicalHistory visitId={visitId} citizenId={citizenId} />}
      {tab === 1 && <CitizenExam visitId={visitId} citizenId={citizenId} />}
      {tab === 2 && <CitizenInvestigations visitId={visitId} citizenId={citizenId} />}
      {tab === 3 && <CitizenPrescriptions visitId={visitId} citizenId={citizenId} />}
    </div>
  );
}
